import { Icon } from 'idea-react';
import { observer } from 'mobx-react';
import { ChangeEvent, Component, DragEvent } from 'react';
import { Button, ListGroup } from 'react-bootstrap';
import { ByteSize } from 'web-utility';

import {
    MAX_FILE_SIZE,
    MAX_FILES,
    SUPPORTED_FORMATS
} from '../../model/Document';
import { t } from '../../model/Translation';

interface Step1SelectFilesProps {
    selectedFiles: File[];
    onFilesChange: (files: File[]) => void;
    onNext: () => void;
}

@observer
export class Step1SelectFiles extends Component<Step1SelectFilesProps> {
    handleFileSelect = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(currentTarget.files || []);
        this.addFiles(files);
    };

    handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const files = Array.from(event.dataTransfer.files);
        this.addFiles(files);
    };

    addFiles = (files: File[]) => {
        const { selectedFiles, onFilesChange } = this.props;

        // Filter and validate files
        const validFiles = files.filter(file => {
            if (file.size > MAX_FILE_SIZE) {
                alert(t('file_exceeds_limit', { name: file.name }));
                return false;
            }
            return true;
        });

        // Limit to max 5 files
        const newFiles = [...selectedFiles, ...validFiles].slice(0, MAX_FILES);
        onFilesChange(newFiles);
    };

    removeFile = (index: number) => {
        const { selectedFiles, onFilesChange } = this.props;
        const newFiles = selectedFiles.filter((_, i) => i !== index);
        onFilesChange(newFiles);
    };

    renderFileItem = (file: File, index: number) => (
        <ListGroup.Item
            key={`${file.name}-${index}`}
            className="d-flex justify-content-between align-items-center"
        >
            <div className="d-flex align-items-center">
                <Icon
                    name="file-earmark-word"
                    className="me-2"
                    style={{
                        fontSize: '24px',
                        color: '#2b579a'
                    }}
                />
                <div>
                    <div>{file.name}</div>
                    <small className="text-muted">
                        {file.type || 'DOCX'} ·{' '}
                        {new ByteSize(file.size).toShortString()}
                    </small>
                </div>
            </div>
            <Button
                variant="link"
                size="sm"
                className="text-muted"
                onClick={() => this.removeFile(index)}
            >
                <Icon name="trash" />
            </Button>
        </ListGroup.Item>
    );

    render() {
        const { selectedFiles, onNext } = this.props;

        return (
            <div>
                <h3 className="mb-4">{t('upload_text_file')}</h3>
                <div
                    className="border border-2 rounded-3 p-5 text-center position-relative"
                    style={{
                        borderStyle: 'dashed',
                        minHeight: '280px',
                        backgroundColor: '#fafafa'
                    }}
                    onDragOver={this.handleDragOver}
                    onDrop={this.handleDrop}
                >
                    <Icon
                        name="cloud-upload"
                        style={{ fontSize: '48px', color: '#d0d0d0' }}
                        className="mb-3"
                    />
                    <p className="mb-2" style={{ fontSize: '16px' }}>
                        {t('drag_or_select')}{' '}
                        <label
                            style={{
                                color: '#4169E1',
                                cursor: 'pointer',
                                textDecoration: 'none'
                            }}
                        >
                            {t('select_file')}
                            <input
                                type="file"
                                className="d-none"
                                accept=".txt,.md,.mdx,.pdf,.html,.htm,.xlsx,.xls,.docx,.csv,.vtt,.properties"
                                multiple
                                onChange={this.handleFileSelect}
                            />
                        </label>
                    </p>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {t('supported_formats')} {SUPPORTED_FORMATS.join('、')}
                        ，{t('max_files_hint')} {MAX_FILES} {t('files_unit')}，
                        {t('max_size_hint')} {MAX_FILE_SIZE / 1024 / 1024} MB。
                    </p>
                </div>

                {selectedFiles.length > 0 && (
                    <div className="mt-4">
                        <ListGroup>
                            {selectedFiles.map(this.renderFileItem)}
                        </ListGroup>
                    </div>
                )}

                <div className="text-end mt-4">
                    <Button
                        variant="primary"
                        style={{
                            minWidth: '120px',
                            backgroundColor: '#4169E1',
                            borderColor: '#4169E1'
                        }}
                        onClick={onNext}
                    >
                        {t('next_step')}{' '}
                        <Icon name="arrow-right" className="ms-1" />
                    </Button>
                </div>
            </div>
        );
    }
}
